import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

const NAV_ITEMS = (t) => [
  { label: t.nav.work, href: "#work" },
  { label: t.nav.services, href: "#services" },
  { label: t.nav.process, href: "#process" },
  { label: t.nav.pricing, href: "#pricing" },
  { label: t.nav.faq, href: "#faq" },
];

export default function Navbar({ t, lang, setLang, waLink }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[rgba(5,5,5,0.7)] backdrop-blur-xl border-b border-[var(--border)]"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="container-x flex items-center justify-between py-5">
        {/* Logo */}
        <a
          href="#top"
          data-testid="logo-link"
          className="font-display font-black text-[var(--text)] tracking-tighter text-xl md:text-2xl"
        >
          Next<span className="text-[var(--accent)]">·</span>Store<span className="text-[var(--accent)] text-sm md:text-base ml-1 font-serif-italic normal-case tracking-normal">.id</span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {NAV_ITEMS(t).map((item, i) => (
            <a
              key={i}
              href={item.href}
              data-testid={`nav-${item.href.slice(1)}`}
              className="font-body text-sm text-[var(--text)] hover:text-[var(--accent)] transition-colors link-underline uppercase tracking-widest"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Language toggle */}
          <div
            className="flex items-center rounded-full border border-[var(--border)] p-0.5 text-xs font-display uppercase tracking-widest"
            data-testid="lang-toggle"
          >
            <button
              onClick={() => setLang("id")}
              data-testid="lang-id"
              className={`px-3 py-1.5 rounded-full transition-all ${
                lang === "id"
                  ? "bg-[var(--text)] text-[var(--bg)]"
                  : "text-[var(--text-muted)] hover:text-[var(--text)]"
              }`}
            >
              ID
            </button>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-3 py-1.5 rounded-full transition-all ${
                lang === "en"
                  ? "bg-[var(--text)] text-[var(--bg)]"
                  : "text-[var(--text-muted)] hover:text-[var(--text)]"
              }`}
            >
              EN
            </button>
          </div>

          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-button"
            className="btn-primary hidden md:inline-flex"
          >
            {t.nav.cta}
            <span aria-hidden>↗</span>
          </a>

          {/* Mobile toggle */}
          <button
            className="lg:hidden text-[var(--text)] p-2"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            <div className="w-6 h-[1.5px] bg-current mb-[6px]" />
            <div className="w-6 h-[1.5px] bg-current" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[var(--bg)] border-t border-[var(--border)]"
          >
            <div className="container-x py-6 flex flex-col gap-4">
              {NAV_ITEMS(t).map((item, i) => (
                <a
                  key={i}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${item.href.slice(1)}`}
                  className="font-display uppercase tracking-widest text-lg text-[var(--text)] py-2"
                >
                  {item.label}
                </a>
              ))}
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-cta"
                className="btn-primary mt-2 self-start"
              >
                {t.nav.cta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
